import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { CellMeasurerCache } from 'react-virtualized';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import DraggableContentWrapper from './DraggableContentWrapper';
import { setIsAthleteAddedItemsSectionExpanded } from '../../ducks/calendarActions';

const AfterRecurringHeader = styled('div')`
  flex-direction: column;
  display: flex;
  margin-bottom: 9px;
  min-height: 48px;
`;

const IconContainer = styled('div')`
  padding-left: ${(props) => {
    if (props.isTvMode) return props.viewType === 'weekView' ? '10px' : '2px';
    return props.viewType === 'weekView' ? '10px' : '28px';
  }};
  cursor: pointer;
  display: flex;
  padding-bottom: 8px;
  padding-top: 8px;
  background-color: #F8F8F8;
  border-radius: 4px;
  ${(props) => !props.isTvMode && props.addPaddingLeft && 'padding-left: 36px;'}
`;

const RecurringText = styled('div')`
  margin-left: 7px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const IconContainerWrapper = styled('div')`
  position: relative;
  background: #444444;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #444444;
  min-height: ${(props) => (props.isTvMode ? '35px' : '25px')};
  min-width: ${(props) => (props.isTvMode ? '35px' : '25px')};
  max-width: ${(props) => (props.isTvMode ? '35px' : '25px')};
  max-height: ${(props) => (props.isTvMode ? '35px' : '25px')};
`;

const BeforeRecurringWorkouts = styled('div')`
  display: flex;
  flex-direction: column;
  transition: max-height 0.3s ease;
  max-width: 100%;
  background-color: #F8F8F8;
  border-radius: 4px;
`;

const LineContainer = styled('div')`
  min-height: 32px;
  position: relative;
  flex-grow: 1;
  margin-left: ${(props) => (props.isTvMode ? '15px' : '10px')};
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 2px;
    background-color: #444444;
  }
  width: 100%;
  overflow: hidden;
`;

const ExpandCollapseContainer = styled('div')`
  :hover {
    cursor: pointer;
    background: rgba(119, 167, 255, 0.2);
  }
  height: 32px;
  width: 32px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 10px;

  .expandCollapseIcon {
    fill: lightgray;
  }

  &:hover .expandCollapseIcon {
    fill: #77A7FF;
  }
`;

const AthleteAddedItemsVirtualRow = React.memo(({
  measure,
  cache,
  date,
  dayKey,
  onDayExpand,
  isDayExpanded,
}) => {
  const dispatch = useDispatch();
  const athleteAddedItemsTooltipId = 'athlete-added-items-expand-tooltip';
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const isTvMode = useSelector((state) => state.calendar.ui.isTvMode);
  const isWeekView = viewType === 'weekView';
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const isAthleteAddedItemsExpandedDayView = useSelector(
    (state) => state.calendar.data.isAthleteAddedItemsSectionExpanded,
  );
  const isAthleteAddedItemsExpandedWeekView = isAthleteAddedItemsExpandedDayView[dayKey];
  const isAthleteAddedItemsExpanded = viewType === 'weekView' ? isAthleteAddedItemsExpandedWeekView : isAthleteAddedItemsExpandedDayView;
  const [afterExpandHovering, setAfterExpandHovering] = useState(false);
  const weekViewHorizontallyExpandedDay = useSelector(
    (state) => state.calendar.data.weekViewExpandedDay,
  );
  const isAthleteAddedItemRowInHorizontallyExpandedDay = dayKey === weekViewHorizontallyExpandedDay;
  const formattedDate = useMemo(
    () => moment(effectiveDate).format('YYYY-MM-DD'),
    [effectiveDate],
  );
  const athleteAddedItems = useSelector(
    (state) => state.calendar.data.calendarItems[formattedDate]?.athleteAddedItems,
  );

  return (
    <AfterRecurringHeader>
      <IconContainer
        onClick={() => {
          if (viewType === 'weekView') {
            dispatch(setIsAthleteAddedItemsSectionExpanded({
              ...isAthleteAddedItemsExpanded,
              [dayKey]: !isAthleteAddedItemsExpanded,
            }));
            if (!isDayExpanded && onDayExpand) {
              onDayExpand();
            }
          } else {
            dispatch(setIsAthleteAddedItemsSectionExpanded(!isAthleteAddedItemsExpanded));
          }
        }}
        viewType={viewType}
        isTvMode={isTvMode}
        addPaddingLeft={((isWeekView
          && String(weekViewHorizontallyExpandedDay) === dayKey))}
      >
        <IconContainerWrapper isTvMode={isTvMode}>
          <IcomoonReact
            iconSet={iconSet}
            size={isTvMode ? 20 : 15}
            icon='user-checkmark'
            color='white'
          />
        </IconContainerWrapper>
        <LineContainer isTvMode={isTvMode}>
          <RecurringText>
            Athlete Added Items (
            {athleteAddedItems?.length}
            )
          </RecurringText>
        </LineContainer>
        {(viewType === 'dayView' || (viewType === 'weekView' && isAthleteAddedItemRowInHorizontallyExpandedDay)) && (
        <ExpandCollapseContainer
          onMouseEnter={() => setAfterExpandHovering(true)}
          onMouseLeave={() => setAfterExpandHovering(false)}
          onClick={() => {
            if (viewType === 'weekView') {
              dispatch(setIsAthleteAddedItemsSectionExpanded({
                ...isAthleteAddedItemsExpanded,
                [dayKey]: !isAthleteAddedItemsExpanded,
              }));
            } else {
              dispatch(setIsAthleteAddedItemsSectionExpanded(!isAthleteAddedItemsExpanded));
            }
          }}
          data-tooltip-id={athleteAddedItemsTooltipId}
        >
          <IcomoonReact
            iconSet={iconSet}
            size={15}
            icon={isAthleteAddedItemsExpanded ? 'collapse' : 'expand2'}
            color={afterExpandHovering ? '#77A7FF' : 'lightgray'}
          />
          <ReactTooltip
            id={athleteAddedItemsTooltipId}
            place='left'
            content={isAthleteAddedItemsExpanded ? 'Collapse' : 'Expand'}
            style={{
              backgroundColor: 'rgba(68, 68, 68, 1)',
              color: 'white',
              fontFamily: 'Nunito Sans',
              fontSize: '13px',
              zIndex: 1000,
            }}
          />
        </ExpandCollapseContainer>
        )}
      </IconContainer>
      <BeforeRecurringWorkouts>
        <div>
          {athleteAddedItems
            ? athleteAddedItems?.map((athleteAddedItem, idx) => (
              <div key={athleteAddedItem.assignedId || idx}>
                <DraggableContentWrapper
                  recurringWorkout={athleteAddedItem}
                  idx={idx}
                  beforeOrAfter='after'
                  measure={measure}
                  isAfterExpanded={isAthleteAddedItemsExpanded}
                  cache={cache}
                  date={date}
                  onDayExpand={onDayExpand}
                  isDayExpanded={isDayExpanded}
                  isAthleteAddedItem
                  athleteAddedItems={athleteAddedItems}
                />
              </div>
            ))
            : null}
        </div>
      </BeforeRecurringWorkouts>
    </AfterRecurringHeader>
  );
});

AthleteAddedItemsVirtualRow.defaultProps = {
  measure: () => {},
  cache: {},
  date: null,
  dayKey: null,
  isDayExpanded: false,
  onDayExpand: () => {},
};

AthleteAddedItemsVirtualRow.propTypes = {
  measure: PropTypes.func,
  cache: PropTypes.instanceOf(CellMeasurerCache),
  date: PropTypes.string,
  dayKey: PropTypes.string,
  isDayExpanded: PropTypes.bool,
  onDayExpand: PropTypes.func,
};

export default AthleteAddedItemsVirtualRow;
